/* Classify the whole description before adding specification dividers. */
(() => {
  const bold = node => node.nodeType === 1 && ['B', 'STRONG'].includes(node.tagName);
  const blank = node => !node.textContent.trim() &&
    !(node.nodeType === 1 && node.querySelector('img,video,iframe,svg,input,button')) &&
    !(node.nodeType === 1 && node.matches('img,video,iframe,svg,input,button'));
  const normalize = text => text.replace(/\s+/g, ' ').trim();
  // Adjacent bold nodes can form one label, e.g. Seriennummer / Datacode:.
  function prefix(paragraph) {
    const nodes = [];
    let label = '';
    for (const node of paragraph.childNodes) {
      if (blank(node)) { nodes.push(node); label += node.textContent; continue; }
      if (!bold(node)) return null;
      nodes.push(node);
      label += node.textContent;
      if (normalize(label).endsWith(':')) {
        const text = normalize(label);
        return text.length <= 80 ? { nodes, text } : null;
      }
    }
    return null;
  }
  function formatDescription(copy) {
    if (copy.dataset.vnkDescriptionChecked) return;
    copy.dataset.vnkDescriptionChecked = 'true';
    const content = copy.querySelector('.toggle-ellipsis__content[data-content]') || copy;
    const paragraphs = [...content.children].filter(node => !blank(node));
    // Prose, lists, media and introductory text retain their original markup.
    if (!paragraphs.length || paragraphs.some(node => node.tagName !== 'P') ||
        [...content.childNodes].some(node => node.nodeType === 3 && node.textContent.trim()) ||
        content.querySelector('ul,ol,table,img,video,iframe,svg') || !prefix(paragraphs[0])) return;
    const fields = paragraphs.map(paragraph => ({ paragraph, label: prefix(paragraph) }));
    const known = new Set(fields.filter(field => field.label).map(field =>
      field.label.text.toLocaleLowerCase('de').replace(/\s*:\s*$/, '')
    ).filter(label => /^(marke|zustand(?: außen| innen)?|material|abmessungen|zubehör|seriennummer(?: \/ datacode)?|datacode)$/.test(label)));
    // Several merchandise attributes establish the schema; other bold labels then work too.
    if (known.size < 3 || !(known.has('material') || known.has('abmessungen'))) return;
    copy.classList.add('vnk-pdp-structured');
    let value = null;
    fields.forEach(({ paragraph, label }) => {
      if (!label) {
        if (value) value.appendChild(paragraph);
        return;
      }
      const row = document.createElement('div');
      row.className = 'vnk-pdp-spec-row';
      const heading = document.createElement('span');
      heading.className = 'vnk-pdp-spec-label';
      value = document.createElement('div');
      value.className = 'vnk-pdp-spec-value';
      paragraph.before(row);
      label.nodes.forEach(node => heading.appendChild(node));
      row.append(heading, value);
      value.appendChild(paragraph);
    });
  }
  // Repair pasted bullet paragraphs without turning prose into merchandise fields.
  function formatProse(copy) {
    if (copy.classList.contains('vnk-pdp-structured') || copy.dataset.vnkProseReady) return;
    copy.dataset.vnkProseReady = 'true';
    copy.classList.add('vnk-pdp-prose');
    const content = copy.querySelector('.toggle-ellipsis__content[data-content]') || copy;
    content.querySelectorAll('meta').forEach(node => node.remove());
    [...content.children].filter(node => node.tagName === 'P' && node.querySelector('br') && /•/.test(node.textContent)).forEach(paragraph => {
      const lines = []; let line = document.createDocumentFragment();
      const flush = () => { if (line.textContent.trim()) lines.push(line); line = document.createDocumentFragment(); };
      function visit(node, parents = []) {
        if (node.nodeName === 'BR') { flush(); return; }
        if (node.nodeName === 'META') return;
        if (node.nodeType === 1 && node.childNodes.length) { node.childNodes.forEach(child => visit(child, [...parents, node])); return; }
        let target = line;
        parents.forEach(parent => { const clone = parent.cloneNode(false); target.appendChild(clone); target = clone; });
        target.appendChild(node.cloneNode(true));
      }
      paragraph.childNodes.forEach(node => visit(node)); flush();
      const replacement = document.createDocumentFragment(); let list = null;
      lines.forEach(fragment => {
        if (/^\s*•/.test(fragment.textContent)) {
          if (!list) { list = document.createElement('ul'); replacement.appendChild(list); }
          const walker = document.createTreeWalker(fragment, NodeFilter.SHOW_TEXT);
          let node; while ((node = walker.nextNode())) { if (node.textContent.trim()) { node.textContent = node.textContent.replace(/^\s*•\s*/, ''); break; } }
          const item = document.createElement('li'); item.appendChild(fragment); list.appendChild(item);
        } else {
          list = null;
          const next = document.createElement('p'); next.appendChild(fragment);
          if ([...next.childNodes].every(node => blank(node) || bold(node))) next.className = 'vnk-copy-subheading';
          replacement.appendChild(next);
        }
      });
      paragraph.replaceWith(replacement);
    });
  }
  function init(root = document) {
    root.querySelectorAll('[data-vnk-product-copy] p').forEach(paragraph => {
      if (paragraph.dataset.vnkFormatted) return;
      paragraph.dataset.vnkFormatted = 'true';
      while (paragraph.firstChild?.nodeName === 'BR' && paragraph.textContent.trim()) {
        paragraph.removeChild(paragraph.firstChild);
      }
      if (!paragraph.textContent.trim() && !paragraph.querySelector('img,video,iframe,svg,input,button')) {
        paragraph.classList.add('vnk-pdp-empty'); return;
      }
      if (paragraph.closest('.vnk-condition-guide,.vnk-shipping-guide') && prefix(paragraph)) {
        paragraph.classList.add('vnk-guide-section');
      }
    });
    root.querySelectorAll('[data-vnk-product-copy].tab-content-0').forEach(copy => {
      if (!copy.querySelector('.vnk-condition-guide,.vnk-shipping-guide')) { formatDescription(copy); formatProse(copy); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* One condition popover per segment, clamped to the viewport with an anchored pointer. */
(() => {
  const instances = new WeakMap();
  const active = new Set();
  let sequence = 0;
  function hide(option) { option.classList.remove('vnk-condition-open'); active.delete(option); }
  function init(root = document) {
    root.querySelectorAll('.zustand-option[data-vnk-condition-tip]').forEach(option => {
      if (instances.has(option)) return;
      const controller = new AbortController();
      const popover = document.createElement('span');
      popover.className = 'vnk-condition-popover';
      popover.id = 'vnk-condition-tip-' + (++sequence);
      popover.setAttribute('role', 'tooltip');
      popover.textContent = option.getAttribute('data-vnk-condition-tip');
      option.appendChild(popover);
      option.classList.add('vnk-condition-enhanced');
      option.setAttribute('aria-describedby', popover.id);
      instances.set(option, controller);
      const show = () => {
        [...active].filter(element => element !== option).forEach(hide);
        // Mobile closed tips do not take up overflow space. Reveal before measuring;
        // positioning and painting still happen together in this task.
        option.classList.add('vnk-condition-open');
        const bounds = option.getBoundingClientRect();
        const width = popover.getBoundingClientRect().width;
        const viewport = window.visualViewport;
        const start = (viewport?.offsetLeft || 0) + 16;
        const end = start + (viewport?.width || window.innerWidth) - 32;
        const triggerCenter = bounds.left + bounds.width / 2;
        const center = Math.max(start + width / 2, Math.min(end - width / 2, triggerCenter));
        popover.style.left = (center - bounds.left) + 'px';
        popover.style.setProperty('--pointer-left', (triggerCenter - center + width / 2) + 'px');
        option.classList.add('vnk-condition-open'); active.add(option);
      };
      option.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') show(); }, { signal: controller.signal });
      option.addEventListener('pointerleave', () => { if (document.activeElement !== option) hide(option); }, { signal: controller.signal });
      option.addEventListener('focus', show, { signal: controller.signal });
      option.addEventListener('blur', () => hide(option), { signal: controller.signal });
      option.addEventListener('keydown', event => { if (event.key === 'Escape') hide(option); }, { signal: controller.signal });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true }); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.zustand-option[data-vnk-condition-tip]').forEach(option => {
      instances.get(option)?.abort(); instances.delete(option); hide(option);
      option.querySelector('.vnk-condition-popover')?.remove();
      option.classList.remove('vnk-condition-enhanced'); option.removeAttribute('aria-describedby');
    });
  });
  window.addEventListener('resize', () => [...active].forEach(hide), { passive: true });
  document.addEventListener('scroll', () => [...active].forEach(hide), { passive: true, capture: true });
})();
